import _ from 'lodash';
import React from 'react';

// Material UI
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Divider from '@material-ui/core/Divider';

// Components
import { SectionWrapper, Spacer } from '../../_core/_ui/structure.components';
import ManageItemsListContainer from '../../_core/ManageItemsList/manage-items-list.container';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { toWeighted } from '../../../lib/helpers/conversion.helpers';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { Link } from 'react-router-dom';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { getPlayerName } from '../../../lib/utils/player-name.service';

interface IApproveRoutes {
  routeSets: { [key: string]: IPlayerAssessment[] };
  routes: IAssessmentStageRoutePlan[];
  playerAssessments: IPlayerAssessment[];
  playerAssessmentsById: { [key: string]: IPlayerAssessment };
  onSubmit: Function;
  loading: boolean;
  fetchError: boolean;
  removePlayerAssessment: Function;
  addPlayerAssessment: Function;
  hasSubmitted: boolean
}

const ApproveRoutes: React.FC<IApproveRoutes> = ({
  routeSets,
  routes,
  playerAssessments,
  playerAssessmentsById,
  onSubmit,
  loading,
  fetchError,
  removePlayerAssessment,
  addPlayerAssessment,
  hasSubmitted
}) => {
  const termLookup = useCompanySportTerminology();

  if (loading && !routes.length) return <Typography>Loading...</Typography>;
  if (fetchError && !routes.length) return <Typography>Unable to approve routes at this time. Please try again later</Typography>;
  if (hasSubmitted) return <Typography>{pluralTerm(termLookup[SportTerminology.ATHLETE])} have been scheduled with the configured {pluralTerm(termLookup[SportTerminology.SESSION])}</Typography>;

  const ROUTE_HEADINGS: { [key: string]: string } = {
    HOLD: 'Hold from Next Grouping',
    CONCLUDE: `Conclude ${termLookup[SportTerminology.ATHLETE]} Assessment`
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box mt={2} display="flex" alignItems="center">
          <Typography>The routes below have been generated based the <Link to="/format">schedule format</Link></Typography>
          <Spacer />
          <PrimaryButton disabled={hasSubmitted} onClick={() => onSubmit(routeSets)}>Approve Routes & Schedule Notifications</PrimaryButton>
        </Box>
      </Grid>
      {_.map(routeSets, (group, action) => {
        return (
          <Grid item xs={12} md={4} key={action}>
            <SectionWrapper>
              <Typography variant="subtitle1">{ROUTE_HEADINGS[action]}</Typography>

              <Box mb={2}><Divider /></Box>

              <Typography variant="subtitle2" gutterBottom>{pluralTerm(termLookup[SportTerminology.ATHLETE])}</Typography>
              <ManageItemsListContainer
                entity="player"
                options={playerAssessments}
                itemLabel={(item: IPlayerAssessment) => `${getPlayerName(item)} - ${item.position.name} - ${toWeighted(item.adjustedScore)}`}
                selectedItems={group ?? []}
                addItem={(item: IPlayerAssessment) => addPlayerAssessment(item, action)}
              >
                {(playerAssessment: IPlayerAssessment) => {
                  const currPlayerAssessment = playerAssessmentsById[playerAssessment?._id];
                  if (!currPlayerAssessment) return <React.Fragment key="none"></React.Fragment>;

                  return (
                    <ListItem key={playerAssessment._id}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" width="100%">
                        <Box>
                          <ListItemText
                            primary={getPlayerName(playerAssessment)}
                            secondary={playerAssessment.position.name}
                          />
                        </Box>
                        <Box display="flex" style={{gap: "0.5rem"}}>
                          <Box pr={4}>
                            <ListItemText
                              primary={`Overall Rank: ${currPlayerAssessment.overallRank}`}
                              secondary={(<Box display="flex" flexDirection="column">
                                <Typography>Position Rank: {currPlayerAssessment.positionRank}</Typography>
                                <Typography>UNBIAS™ Result: {toWeighted(currPlayerAssessment.adjustedScore)}</Typography>
                              </Box>)} />
                          </Box>
                          <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="delete"
                                        onClick={() => removePlayerAssessment(playerAssessment, action)}>
                              <DeleteIcon/>
                            </IconButton>
                          </ListItemSecondaryAction>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                }}
              </ManageItemsListContainer>
            </SectionWrapper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default ApproveRoutes;
